import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { householdApi } from "../../services/api";
import { apiErrorMessage, parseApiError } from "../../services/apiError";
import PageHeader from "../PageHeader";
import { displayName } from "../tasks/userDisplay";

/**
 * Your household (household sharing Q4): the people you share with by
 * default — "Household" projects and drawings, pins, the people directory
 * — and the agents they own. Invite someone by their exact username or
 * email; nothing is shared until they accept. Anyone can leave.
 */
function HouseholdPage() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusyState] = useState(null); // which action is in flight
  // A double-click lands before React re-renders the disabled button, so
  // the in-flight guard reads a ref, not state (review of #359, L5).
  const busyRef = useRef(null);
  const setBusy = (key) => {
    busyRef.current = key;
    setBusyState(key);
  };
  const [name, setName] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [who, setWho] = useState("");
  const [whoError, setWhoError] = useState(null);
  const [leaveArmed, setLeaveArmed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { data } = await householdApi.get();
      setState(data);
    } catch (err) {
      setLoadError(apiErrorMessage(err, "Couldn't load your household."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // An armed "Leave" doesn't sit there waiting for a stray click.
  useEffect(() => {
    if (!leaveArmed) return undefined;
    const t = setTimeout(() => setLeaveArmed(false), 5000);
    return () => clearTimeout(t);
  }, [leaveArmed]);

  // One membership change at a time; each answers with the whole page.
  const run = async (key, call, { gone } = {}) => {
    if (busyRef.current) return false;
    setBusy(key);
    setError(null);
    try {
      const { data } = await call();
      if (data) setState(data);
      return true;
    } catch (err) {
      if (gone && err?.response?.status === 404) {
        // withdrawn, declined elsewhere, or its sender has left
        setError(gone);
        load();
      } else {
        setError(apiErrorMessage(err, "That didn't work. Try again."));
      }
      return false;
    } finally {
      setBusy(null);
    }
  };

  const start = async (event) => {
    event.preventDefault();
    if (!name.trim()) return;
    if (await run("start", () => householdApi.start(name.trim()))) setName("");
  };

  const rename = async (event) => {
    event.preventDefault();
    if (!name.trim()) return;
    if (await run("rename", () => householdApi.rename(name.trim()))) {
      setRenaming(false);
      setName("");
    }
  };

  const invite = async (event) => {
    event.preventDefault();
    const q = who.trim();
    if (!q || busyRef.current) return;
    setBusy("invite");
    setWhoError(null);
    setError(null);
    try {
      const { data } = await householdApi.invite(q);
      setState((cur) => ({
        ...cur,
        household: {
          ...cur.household,
          invites: [
            data,
            ...cur.household.invites.filter((i) => i.id !== data.id),
          ],
        },
      }));
      setWho("");
    } catch (err) {
      const { formError, fieldErrors } = parseApiError(
        err,
        "Couldn't send that invitation. Try again.",
      );
      setWhoError(
        fieldErrors.who ? [].concat(fieldErrors.who).join(" ") : formError,
      );
    } finally {
      setBusy(null);
    }
  };

  const removeInvite = async (id, key) => {
    if (busyRef.current) return;
    setBusy(key);
    setError(null);
    try {
      await householdApi.removeInvite(id);
      await load();
    } catch (err) {
      setError(apiErrorMessage(err, "That didn't work. Try again."));
    } finally {
      setBusy(null);
    }
  };

  const leave = async () => {
    if (!leaveArmed) {
      setLeaveArmed(true);
      return;
    }
    setLeaveArmed(false);
    await run("leave", () => householdApi.leave());
  };

  if (loading && !state) {
    return (
      <div className="py-12 text-center" role="status">
        <Loader2 className="inline size-6 animate-spin text-muted-foreground" />
        <span className="sr-only">Loading your household…</span>
      </div>
    );
  }

  if (loadError && !state) {
    return (
      <div className="page-container mt-6 pt-4">
        <Alert variant="destructive">
          <AlertDescription className="flex items-center gap-3">
            <span className="flex-1">{loadError}</span>
            <Button variant="outline" size="sm" onClick={load}>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const household = state?.household ?? null;
  const invitations = state?.invitations ?? [];

  return (
    <div className="page-container mt-6 pt-4">
      <PageHeader
        title="Household"
        subtitle="The people you share with by default — and the agents they own. Household projects and drawings, pins and the people list reach them; nobody outside does."
      />

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {invitations.length > 0 && (
        <section aria-labelledby="invitations-heading" className="mb-6">
          <h2 id="invitations-heading" className="mb-2 text-lg font-semibold">
            Invitations for you
          </h2>
          <ul className="grid gap-2">
            {invitations.map((inv) => (
              <li key={inv.id} className="rounded-lg border bg-card p-4">
                <p>
                  <strong>
                    {inv.invited_by ? displayName(inv.invited_by) : "Someone"}
                  </strong>{" "}
                  invited you to join <strong>{inv.household.name}</strong>.
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {household
                    ? `Joining means leaving ${household.name}: you'll stop seeing what's shared with it, and it stops seeing yours. Everything you and your agents have set to "Household" becomes visible to ${inv.household.name}'s people and their agents instead.`
                    : `Everything you and your agents have set to "Household" becomes visible to ${inv.household.name}'s people and their agents, and you'll see what they share with it.`}
                </p>
                <div className="mt-3 flex gap-2">
                  <Button
                    className="h-11 md:h-9"
                    disabled={Boolean(busy)}
                    onClick={() =>
                      run(
                        `accept-${inv.id}`,
                        () => householdApi.accept(inv.id),
                        {
                          gone: "That invitation isn't open any more — it was withdrawn, or the person who sent it has left.",
                        },
                      )
                    }
                  >
                    {busy === `accept-${inv.id}` && (
                      <Loader2 className="animate-spin" />
                    )}
                    Join {inv.household.name}
                  </Button>
                  <Button
                    variant="outline"
                    className="h-11 md:h-9"
                    disabled={Boolean(busy)}
                    onClick={() => removeInvite(inv.id, `decline-${inv.id}`)}
                  >
                    Decline
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {!household ? (
        <section className="rounded-lg border bg-card p-4">
          <h2 className="text-lg font-semibold">
            You&apos;re not in a household
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Anything you set to &ldquo;Household&rdquo; stays with you and your
            agents until you&apos;re in one. Start one and invite people, or ask
            someone to invite you.
          </p>
          <form onSubmit={start} className="mt-3 grid gap-2 sm:max-w-md">
            <Label htmlFor="household-name">Household name</Label>
            <div className="flex gap-2">
              <Input
                id="household-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={120}
                placeholder="e.g. The Bodines"
              />
              <Button
                type="submit"
                className="h-11 md:h-9"
                disabled={!name.trim() || Boolean(busy)}
              >
                {busy === "start" && <Loader2 className="animate-spin" />}
                Start
              </Button>
            </div>
          </form>
        </section>
      ) : (
        <div className="grid gap-6">
          <section className="rounded-lg border bg-card p-4">
            {renaming ? (
              <form onSubmit={rename} className="grid gap-2 sm:max-w-md">
                <Label htmlFor="household-rename">Household name</Label>
                <div className="flex gap-2">
                  <Input
                    id="household-rename"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    maxLength={120}
                  />
                  <Button
                    type="submit"
                    className="h-11 md:h-9"
                    disabled={!name.trim() || Boolean(busy)}
                  >
                    Save
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-11 md:h-9"
                    onClick={() => {
                      setRenaming(false);
                      setName("");
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-lg font-semibold">{household.name}</h2>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setName(household.name);
                    setRenaming(true);
                  }}
                >
                  Rename
                </Button>
              </div>
            )}

            <h3 className="mt-4 text-sm font-medium">People</h3>
            <ul className="mt-2 flex flex-wrap gap-2" aria-label="People">
              {household.members.map((m) => (
                <li
                  key={m.id}
                  className="rounded-full border bg-muted px-3 py-1 text-sm"
                >
                  {displayName(m)}
                </li>
              ))}
            </ul>
            <p className="mt-2 text-sm text-muted-foreground">
              Each person&apos;s agents come with them: they see what the
              household shares, too.
            </p>
          </section>

          <section
            aria-labelledby="invite-heading"
            className="rounded-lg border bg-card p-4"
          >
            <h2 id="invite-heading" className="text-lg font-semibold">
              Invite someone
            </h2>
            <form onSubmit={invite} className="mt-2 grid gap-2 sm:max-w-md">
              <Label htmlFor="household-invite">
                Their exact username or email
              </Label>
              <div className="flex gap-2">
                <Input
                  id="household-invite"
                  value={who}
                  onChange={(e) => setWho(e.target.value)}
                  placeholder="username or email"
                  autoComplete="off"
                  aria-invalid={Boolean(whoError)}
                  aria-describedby={
                    whoError ? "household-invite-error" : undefined
                  }
                />
                <Button
                  type="submit"
                  variant="secondary"
                  className="h-11 md:h-9"
                  disabled={!who.trim() || Boolean(busy)}
                >
                  {busy === "invite" && <Loader2 className="animate-spin" />}
                  Invite
                </Button>
              </div>
              {whoError && (
                <p
                  id="household-invite-error"
                  className="text-sm text-destructive"
                >
                  {whoError}
                </p>
              )}
              <p className="text-sm text-muted-foreground">
                They&apos;ll see an invitation here. Nothing is shared until
                they accept. They need an account on this mono already.
              </p>
            </form>

            {household.invites.length > 0 && (
              <>
                <h3 className="mt-4 text-sm font-medium">Waiting to accept</h3>
                <ul className="mt-2 grid gap-2">
                  {household.invites.map((inv) => (
                    <li
                      key={inv.id}
                      className="flex items-center justify-between gap-3 rounded-lg border p-3"
                    >
                      <span>
                        {displayName(inv.person)}
                        <span className="text-sm text-muted-foreground">
                          {" "}
                          — invited by{" "}
                          {inv.invited_by
                            ? displayName(inv.invited_by)
                            : "someone"}
                        </span>
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={Boolean(busy)}
                        aria-label={`Withdraw ${displayName(inv.person)}'s invitation`}
                        onClick={() =>
                          removeInvite(inv.id, `withdraw-${inv.id}`)
                        }
                      >
                        Withdraw
                      </Button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>

          <section className="rounded-lg border bg-card p-4">
            <h2 className="text-lg font-semibold">Leave {household.name}</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              You and your agents stop seeing what&apos;s shared with the
              household, and it stops seeing what you shared with it. Tasks you
              added to its projects stay there. Anything you or your agents had
              claimed there is let go.
              {household.members.length === 1 &&
                " You're the last one here, so the household closes and its invitations go with it."}
            </p>
            <Button
              variant="destructive"
              className="mt-3 h-11 md:h-9"
              disabled={Boolean(busy)}
              onClick={leave}
            >
              {busy === "leave" && <Loader2 className="animate-spin" />}
              {leaveArmed
                ? `Confirm: leave ${household.name}`
                : "Leave household"}
            </Button>
          </section>
        </div>
      )}
    </div>
  );
}

export default HouseholdPage;
